import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { getLocalApiUrl, authFetch } from '../config/api'

const FeatureFlagContext = createContext({})

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext)
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider')
  }
  return context
}

/**
 * Check if a specific flag is enabled.
 * Convenience hook for single flag checks.
 */
export const useFeatureFlag = (flagKey) => {
  const { isEnabled } = useFeatureFlags()
  return isEnabled(flagKey)
}

export const FeatureFlagProvider = ({ children }) => {
  const [flags, setFlags] = useState({})
  const [loading, setLoading] = useState(true)

  // Load flags from localStorage on mount (cached from login)
  useEffect(() => {
    try {
      const stored = localStorage.getItem('paysafego_feature_flags')
      if (stored) {
        setFlags(JSON.parse(stored))
      }
    } catch (_) { /* ignore parse errors */ }
    setLoading(false)
  }, [])

  /**
   * Set flags from login response data.
   * Called by AuthContext after successful login.
   */
  const setFlagsFromLogin = useCallback((featureFlags) => {
    const flagMap = featureFlags || {}
    setFlags(flagMap)
    localStorage.setItem('paysafego_feature_flags', JSON.stringify(flagMap))
  }, [])

  /**
   * Refresh flags from the API (e.g., after admin changes).
   */
  const refreshFlags = useCallback(async () => {
    try {
      const res = await authFetch(getLocalApiUrl('/api/feature-flags'))
      if (res.ok) {
        const data = await res.json()
        if (data.success && data.flags) {
          setFlags(data.flags)
          localStorage.setItem('paysafego_feature_flags', JSON.stringify(data.flags))
        }
      }
    } catch (err) {
      console.warn('Failed to refresh feature flags:', err)
    }
  }, [])

  /**
   * Check if a flag is enabled. Supports:
   * - Exact key: 'module:profile-safe'
   * - Page key: 'page:profile-safe/onboarding'
   * - Feature key: 'feature:aml-screening'
   *
   * If the flag doesn't exist in the map, defaults to true (fail-open for
   * features not yet registered as flags).
   */
  const isEnabled = useCallback((flagKey) => {
    if (!flagKey) return true
    if (flags[flagKey] !== undefined) return flags[flagKey]
    // Fail-open: unregistered flags are treated as enabled
    return true
  }, [flags])

  /**
   * Check if a module is enabled (convenience).
   */
  const isModuleEnabled = useCallback((moduleName) => {
    return isEnabled(`module:${moduleName}`)
  }, [isEnabled])

  /**
   * Check if a page route is enabled.
   * Checks both the page flag and its parent module flag.
   */
  const isPageEnabled = useCallback((routePath) => {
    // Legacy URL redirects to payout builder; use builder's flags (admin duplicate removed).
    const path = String(routePath || '').replace(/\/$/, '') || '/'
    const pathForFlags = path === '/admin/venue-payouts' ? '/payout-safe/builder' : path

    // Check module-level flag first
    // Extract module from route: '/profile-safe/onboarding' -> 'profile-safe'
    const parts = pathForFlags.replace(/^\//, '').split('/')
    const moduleName = parts[0]
    if (!isEnabled(`module:${moduleName}`)) return false

    // Check page-level flag
    const pageKey = `page:${pathForFlags.replace(/^\//, '')}`
    return isEnabled(pageKey)
  }, [isEnabled])

  /**
   * Clear flags (on logout).
   */
  const clearFlags = useCallback(() => {
    setFlags({})
    localStorage.removeItem('paysafego_feature_flags')
  }, [])

  const value = {
    flags,
    loading,
    isEnabled,
    isModuleEnabled,
    isPageEnabled,
    setFlagsFromLogin,
    refreshFlags,
    clearFlags,
  }

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  )
}

export default FeatureFlagContext
